// ─── LeadAging.js ───────────────────────────────────────────────────────────
// Reports → "Lead Aging" tab
// Selected period mein BANI leads ko ek stage se doosre stage tak pahunchne
// mein kitna AVERAGE time (days) laga — SM / TL wise + Overall.
// "n leads" link par click karne se un leads ki poori detail popup mein aati hai.
import React, { useState, useEffect, useCallback } from "react";
import { getStageAging, getStageAgingDetails } from "../api";

const METRICS = [
  { key: "new_to_prospect", label: "New → Prospect" },
  { key: "inbound_to_prospect", label: "Inbound → Prospect" },
  { key: "inbound_to_meeting", label: "Inbound → Meeting" },
  { key: "inbound_to_site_visit", label: "Inbound → Site Visit" },
  { key: "prospect_to_meeting", label: "Prospect → Meeting" },
  { key: "prospect_to_site_visit", label: "Prospect → Site Visit" },
];

const pad = (n) => String(n).padStart(2, "0");

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const monthStartStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-01`;
};

// Hamesha DAYS mein dikhao (1 din se kam ho to 2 decimal)
function formatAge(days) {
  if (days === null || days === undefined) return "—";
  const d = Number(days);
  if (d > 0 && d < 0.01) return "< 0.01 days";
  return `${d < 1 ? d.toFixed(2) : d.toFixed(1)} days`;
}

const labelStyle = {
  display: "block",
  fontSize: 11,
  fontWeight: 600,
  color: "#6B7280",
  marginBottom: 6,
  textTransform: "uppercase",
  letterSpacing: ".4px",
};

const inputStyle = {
  height: 38,
  border: "1px solid #E5E7EB",
  borderRadius: 8,
  padding: "0 10px",
  background: "#fff",
};

const thStyle = {
  padding: "12px 14px",
  textAlign: "center",
  fontSize: 11,
  fontWeight: 700,
  color: "#6B7280",
  borderBottom: "1px solid #E5E7EB",
  whiteSpace: "nowrap",
};

const detailTh = { padding: 12, textAlign: "left", whiteSpace: "nowrap" };
const detailTd = { padding: 12, borderTop: "1px solid #eee" };

function LeadsCreatedCell({ row, strong }) {
  return (
    <td
      style={{
        padding: "10px 14px",
        textAlign: "center",
        borderBottom: "1px solid #F0F0F0",
      }}
    >
      <div
        style={{
          fontSize: 14,
          fontWeight: strong ? 800 : 700,
          color: "#111827",
        }}
      >
        {Number(row.leads_created || 0).toLocaleString("en-IN")}
      </div>

      <div style={{ fontSize: 10, color: "#9CA3AF", marginTop: 2 }}>
        in selected period
      </div>
    </td>
  );
}

function AgeCell({ row, metricKey, strong, onOpen }) {
  const count = Number(row[`${metricKey}_count`] || 0);
  const days = row[`${metricKey}_avg_days`];
  const clickable = count > 0 && typeof onOpen === "function";

  const content = (
    <>
      <div
        style={{
          fontSize: 13,
          fontWeight: strong ? 800 : 700,
          color: count > 0 ? "#111827" : "#9CA3AF",
        }}
      >
        {count > 0 ? formatAge(days) : "—"}
      </div>

      <div
        style={{
          fontSize: 11,
          marginTop: 2,
          color: clickable ? "#1D9E75" : "#9CA3AF",
          fontWeight: clickable ? 700 : 400,
          textDecoration: clickable ? "underline" : "none",
        }}
      >
        {count} {count === 1 ? "lead" : "leads"}
      </div>
    </>
  );

  return (
    <td
      style={{
        padding: "10px 14px",
        textAlign: "center",
        borderBottom: "1px solid #F0F0F0",
      }}
    >
      {clickable ? (
        <button
          type="button"
          onClick={() => onOpen(row, metricKey)}
          title="View lead details"
          style={{
            border: "none",
            background: "transparent",
            padding: 0,
            cursor: "pointer",
            font: "inherit",
            textAlign: "center",
          }}
        >
          {content}
        </button>
      ) : (
        content
      )}
    </td>
  );
}

export default function LeadAging({ salesManagers = [], isSalesManager = false }) {
  const [from, setFrom] = useState(monthStartStr());
  const [to, setTo] = useState(todayStr());
  const [assignedTo, setAssignedTo] = useState("");

  // Jo filters last "Apply" par lage the (popup inhi se khulta hai)
  const [applied, setApplied] = useState({
    from: monthStartStr(),
    to: todayStr(),
    assignedTo: "",
  });

  const [rows, setRows] = useState([]);
  const [overall, setOverall] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // ---- Detail popup ----
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [detailData, setDetailData] = useState([]);
  const [detailTitle, setDetailTitle] = useState("");
  const [detailShowManager, setDetailShowManager] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const filters = {};
      if (from) filters.from = from;
      if (to) filters.to = to;
      if (assignedTo) filters.assigned_to = assignedTo;

      const response = await getStageAging(filters);

      const list = Array.isArray(response.data?.data) ? response.data.data : [];

      // Sirf wahi SM/TL dikhao jinki selected period mein koi lead bani ho
      setRows(list.filter((r) => Number(r.leads_created || 0) > 0));
      setOverall(response.data?.overall || null);
      setApplied({ from, to, assignedTo });
    } catch (err) {
      console.error("LEAD AGING LOAD ERROR:", err);
      setRows([]);
      setOverall(null);
      setError(err.response?.data?.error || "Could not load lead aging report");
    } finally {
      setLoading(false);
    }
  }, [from, to, assignedTo]);

  // Tab khulte hi ek baar load
  useEffect(() => {
    load();
  }, []);

  const openDetails = async (row, metricKey) => {
    const metric = METRICS.find((m) => m.key === metricKey);

    setDetailOpen(true);
    setDetailLoading(true);
    setDetailError("");
    setDetailData([]);
    setDetailTitle(`${row.sales_manager} — ${metric ? metric.label : ""}`);
    setDetailShowManager(!row.sales_manager_id);

    try {
      const filters = {
        from: applied.from,
        to: applied.to,
        metric: metricKey,
      };

      if (row.sales_manager_id) {
        filters.sales_manager_id = row.sales_manager_id;
      } else if (applied.assignedTo) {
        filters.assigned_to = applied.assignedTo;
      }

      const response = await getStageAgingDetails(filters);

      setDetailData(Array.isArray(response.data?.data) ? response.data.data : []);
    } catch (err) {
      console.error("LEAD AGING DETAILS ERROR:", err);
      setDetailError(
        err.response?.data?.error || "Could not load lead details"
      );
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <div>
      {/* ========== FILTER BAR ========== */}
      <div
        className="card"
        style={{
          marginBottom: 16,
          padding: 16,
          display: "flex",
          alignItems: "flex-end",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div>
          <label style={labelStyle}>Lead Created From</label>
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            style={inputStyle}
          />
        </div>

        <div>
          <label style={labelStyle}>Lead Created To</label>
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            style={inputStyle}
          />
        </div>

        {!isSalesManager && (
          <div>
            <label style={labelStyle}>Sales Manager</label>
            <select
              value={assignedTo}
              onChange={(e) => setAssignedTo(e.target.value)}
              style={{ ...inputStyle, minWidth: 200 }}
            >
              <option value="">All Sales Managers / Team Leads</option>
              {salesManagers.map((manager) => (
                <option key={manager.id} value={manager.id}>
                  {manager.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <button
          type="button"
          onClick={load}
          disabled={loading}
          style={{
            height: 38,
            padding: "0 22px",
            border: "none",
            borderRadius: 8,
            background: "#1D9E75",
            color: "#fff",
            fontWeight: 600,
            cursor: loading ? "not-allowed" : "pointer",
            opacity: loading ? 0.7 : 1,
          }}
        >
          {loading ? "Loading..." : "Apply Filters"}
        </button>
      </div>

      {error && (
        <div
          style={{
            marginBottom: 16,
            padding: "10px 14px",
            borderRadius: 8,
            background: "#FCEBEB",
            color: "#A32D2D",
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      {/* ========== TABLE ========== */}
      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <div
          style={{
            padding: "14px 16px",
            borderBottom: "1px solid #F0F0F0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>
              Lead Aging Report
            </div>
            <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2 }}>
              Average time taken by leads created in the selected period to
              move from one stage to the next
            </div>
          </div>

          <div style={{ fontSize: 11, color: "#6B7280" }}>
            {rows.length} Sales Managers / Team Leads
          </div>
        </div>

        <div style={{ overflowX: "auto" }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: "center", color: "#6B7280" }}>
              Loading lead aging report...
            </div>
          ) : (
            <table
              style={{
                width: "100%",
                minWidth: 1200,
                borderCollapse: "collapse",
              }}
            >
              <thead>
                <tr style={{ background: "#F9FAFB" }}>
                  <th
                    style={{
                      ...thStyle,
                      textAlign: "left",
                      position: "sticky",
                      left: 0,
                      background: "#F9FAFB",
                      zIndex: 2,
                      minWidth: 200,
                    }}
                  >
                    Sales Manager / Team Lead
                  </th>

                  <th style={thStyle}>Leads Created</th>

                  {METRICS.map((metric) => (
                    <th key={metric.key} style={thStyle}>
                      {metric.label}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={METRICS.length + 2}
                      style={{
                        padding: 40,
                        textAlign: "center",
                        color: "#9CA3AF",
                      }}
                    >
                      No leads found in the selected period.
                    </td>
                  </tr>
                ) : (
                  <>
                    {/* OVERALL (AVERAGE) ROW */}
                    {overall && (
                      <tr
                        style={{
                          background: "#F3F4F6",
                          borderBottom: "2px solid #D1D5DB",
                        }}
                      >
                        <td
                          style={{
                            padding: "10px 14px",
                            fontSize: 12,
                            fontWeight: 800,
                            color: "#111827",
                            position: "sticky",
                            left: 0,
                            background: "#F3F4F6",
                            zIndex: 1,
                            whiteSpace: "nowrap",
                          }}
                        >
                          Overall (Average)
                        </td>

                        <LeadsCreatedCell row={overall} strong />

                        {METRICS.map((metric) => (
                          <AgeCell
                            key={metric.key}
                            row={overall}
                            metricKey={metric.key}
                            strong
                            onOpen={openDetails}
                          />
                        ))}
                      </tr>
                    )}

                    {/* SM / TL ROWS */}
                    {rows.map((row) => (
                      <tr key={row.sales_manager_id}>
                        <td
                          style={{
                            padding: "10px 14px",
                            fontSize: 12,
                            fontWeight: 600,
                            color: "#111827",
                            borderBottom: "1px solid #F0F0F0",
                            position: "sticky",
                            left: 0,
                            background: "#fff",
                            zIndex: 1,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {row.sales_manager}
                        </td>

                        <LeadsCreatedCell row={row} />

                        {METRICS.map((metric) => (
                          <AgeCell
                            key={metric.key}
                            row={row}
                            metricKey={metric.key}
                            onOpen={openDetails}
                          />
                        ))}
                      </tr>
                    ))}
                  </>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div style={{ marginTop: 10, fontSize: 11, color: "#9CA3AF" }}>
        Leads Created = selected period mein bani total leads. Har stage cell
        mein average din hain, aur neeche wo leads jinhone dono stages touch
        kiye (green link par click karke lead-wise detail dekho). Jis stage tak
        abhi koi lead pahunchi nahi, wahan "—" dikhega.
      </div>

      {/* ========== DETAIL POPUP ========== */}
      {detailOpen && (
        <div
          onClick={() => setDetailOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            zIndex: 10000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(1100px, 95vw)",
              maxHeight: "85vh",
              background: "#fff",
              borderRadius: 14,
              boxShadow: "0 20px 50px rgba(0,0,0,0.2)",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                padding: "16px 20px",
                borderBottom: "1px solid #E5E7EB",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#111827" }}>
                  {detailTitle}
                </div>

                <div style={{ fontSize: 12, color: "#6B7280", marginTop: 4 }}>
                  Leads created {applied.from} to {applied.to} ·{" "}
                  {detailData.length} leads
                </div>
              </div>

              <button
                type="button"
                onClick={() => setDetailOpen(false)}
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: "50%",
                  border: "1px solid #E5E7EB",
                  background: "#fff",
                  cursor: "pointer",
                  fontSize: 20,
                }}
              >
                ×
              </button>
            </div>

            <div style={{ overflow: "auto", flex: 1 }}>
              {detailLoading ? (
                <div style={{ padding: 40, textAlign: "center", color: "#6B7280" }}>
                  Loading details...
                </div>
              ) : detailError ? (
                <div style={{ padding: 40, textAlign: "center", color: "#A32D2D" }}>
                  {detailError}
                </div>
              ) : detailData.length === 0 ? (
                <div style={{ padding: 40, textAlign: "center", color: "#6B7280" }}>
                  No leads found.
                </div>
              ) : (
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: 13,
                  }}
                >
                  <thead>
                    <tr style={{ background: "#F9FAFB" }}>
                      <th style={detailTh}>Lead Name</th>
                      <th style={detailTh}>Phone</th>
                      <th style={detailTh}>Stage</th>
                      <th style={detailTh}>Campaign</th>
                      {detailShowManager && (
                        <th style={detailTh}>Sales Manager</th>
                      )}
                      <th style={detailTh}>Assigned On</th>
                      <th style={detailTh}>Moved On</th>
                      <th style={detailTh}>Days Taken</th>
                    </tr>
                  </thead>

                  <tbody>
                    {detailData.map((item) => (
                      <tr key={item.lead_id}>
                        <td style={detailTd}>{item.lead_name || "-"}</td>
                        <td style={detailTd}>{item.lead_phone || "-"}</td>
                        <td style={detailTd}>{item.current_stage || "-"}</td>
                        <td style={detailTd}>{item.campaign_name || "-"}</td>
                        {detailShowManager && (
                          <td style={detailTd}>{item.sales_manager || "-"}</td>
                        )}
                        <td style={detailTd}>
                          {item.assigned_at
                            ? new Date(item.assigned_at).toLocaleString("en-IN")
                            : "-"}
                        </td>
                        <td style={detailTd}>
                          {item.moved_at
                            ? new Date(item.moved_at).toLocaleString("en-IN")
                            : "-"}
                        </td>
                        <td
                          style={{
                            ...detailTd,
                            fontWeight: 700,
                            color: "#1D9E75",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {formatAge(item.days_taken)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}